import React, { useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useRAGStore } from '../store/ragStore';
import { setUploadSuccessCallback } from '../store/ragStore';
import { useTabNavigation } from '../contexts/TabNavigationContext';
import { Upload, File, X } from 'lucide-react';
import { FileUpload } from '../types/rag';


const FileUploadTab: React.FC = () => {
  const {
    currentContext,
    fileUploads,
    uploadFile,
    removeFileUpload,
    isOperationInProgress,
    currentOperation,
  } = useRAGStore();
  
  const { navigateToTab } = useTabNavigation();

  // Create a stable callback function
  const handleUploadSuccess = useCallback(() => {
    navigateToTab('File List');
  }, [navigateToTab]);

  // Set up the upload success callback
  useEffect(() => {
    setUploadSuccessCallback(handleUploadSuccess);

    // Cleanup: remove callback when component unmounts
    return () => {
      setUploadSuccessCallback(null);
    };
  }, [handleUploadSuccess]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    // Allow non-blocking upload - don't check isOperationInProgress
    acceptedFiles.forEach(file => {
      uploadFile(file);
    });
  }, [uploadFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/json': ['.json'],
      // JSONL files are frequently reported with an empty or octet-stream MIME
      // type by the browser, so list the extension under several MIME keys to
      // ensure react-dropzone accepts them by extension regardless.
      'application/jsonl': ['.jsonl'],
      'application/x-ndjson': ['.jsonl'],
    },
    multiple: true,
    // Remove disabled restriction to allow non-blocking upload
  });

    if (!currentContext) {
    return (
      <div className="card">
        <div className="text-center py-12">
          <Upload className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Context Selected
          </h3>
          <p className="text-gray-600">
            Please go to the Context Management tab to select a context before uploading files.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upload Zone */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">File Upload</h3>
        
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors duration-200 ${
            isDragActive
              ? 'border-blue-400 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
          }`}
        >
          <input {...getInputProps()} />
          
          <Upload className="mx-auto h-12 w-12 mb-4 text-gray-400" />
          
          {isDragActive ? (
            <p className="text-lg text-blue-600">Drop the files here...</p>
          ) : (
            <div>
              <p className="text-lg text-gray-600 mb-2">
                Drag and drop files here, or click to select files
              </p>
              <p className="text-sm text-gray-500">
                Supported formats: PDF, JSON, JSONL • Multiple files can be uploaded simultaneously
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Upload Progress */}
      {fileUploads && fileUploads.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Upload Progress</h3>
          
          <div className="space-y-3">
            {fileUploads?.map((upload: FileUpload) => (
              <div
                key={upload.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
              >
                <div className="flex items-center space-x-3 flex-1">
                  <File className="h-5 w-5 text-gray-400" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {upload.file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(upload.file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  {/* Progress Bar */}
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${upload.progress}%` }}
                    />
                  </div>

                  {/* Status */}
                  <span className={`text-sm px-2 py-1 rounded-full ${
                    upload.status === 'success'
                      ? 'bg-green-100 text-green-800'
                      : upload.status === 'error'
                      ? 'bg-red-100 text-red-800'
                      : upload.status === 'uploading'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {upload.status}
                  </span>

                  {/* Remove Button */}
                  <button
                    onClick={() => removeFileUpload(upload.id)}
                    disabled={isOperationInProgress}
                    className={`transition-colors duration-200 ${
                      isOperationInProgress 
                        ? 'text-gray-300 cursor-not-allowed' 
                        : 'text-gray-400 hover:text-red-500'
                    }`}
                    title={isOperationInProgress ? "Cannot remove during operation" : "Remove upload"}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Context Info */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">Current Context</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-700">Context ID</p>
            <p className="text-gray-900">{currentContext.contextId}</p>
          </div>
          
          <div>
            <p className="text-sm font-medium text-gray-700">RAG Items</p>
                              <p className="text-gray-900">{currentContext.ragItems?.length || 0}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileUploadTab;
